import { GitBranch } from 'lucide-react';

import type { AssumptionSnapshotProps } from '@shared/widget-schemas';
import { cn } from '@/lib/utils';
import { ChatCard, SnapHead, SparkStatsWell, deriveSpark } from './chat-widget-ui';
import { capitalizeFirst } from './textFormat';
import { SkBar, SkChip } from './skeletons';

const BLUE = 'var(--color-blue-400)';

export function AssumptionSnapshotWidget({
  title,
  summary,
  confidence,
  lvl,
  trend,
  sparkline,
  interviews,
  signals,
}: AssumptionSnapshotProps) {
  const up = trend >= 0;
  const spark = sparkline && sparkline.length >= 2 ? sparkline : deriveSpark(confidence, trend);

  return (
    <ChatCard className="flex flex-col gap-3 p-4 text-sm">
      <SnapHead
        icon={GitBranch}
        iconColor={BLUE}
        eyebrow="Assumption"
        title={title}
        confidence={confidence}
        lvl={lvl}
        trend={trend}
      />

      {/* An assumption is a child of a bet — the in-well link opens that bet. */}
      <SparkStatsWell spark={spark} up={up} interviews={interviews} signals={signals} linkLabel="Open bet" />

      {summary && (
        <p className="text-[12px] leading-[1.55] text-muted-foreground text-pretty">{capitalizeFirst(summary)}</p>
      )}
    </ChatCard>
  );
}

export function AssumptionSnapshotSkeleton({ className }: { className?: string }) {
  return (
    <ChatCard className={cn('flex flex-col gap-3 p-4', className)}>
      <div className="flex items-start gap-[11px]">
        <SkBar width="30px" height="30px" radius="8px" />
        <div className="flex flex-1 flex-col gap-[5px]">
          <SkBar width="90px" height="8px" />
          <SkBar width="70%" height="13px" />
        </div>
        <div className="flex flex-col items-end gap-[5px]">
          <SkChip width="44px" height="18px" />
          <SkBar width="34px" height="8px" />
        </div>
      </div>
      <div
        className="flex items-center gap-3 rounded-[11px] border border-border px-3 py-2.5"
        style={{ background: 'var(--tinted-bg)' }}
      >
        <SkBar width="116px" height="26px" radius="6px" />
        <div className="flex flex-col gap-1.5">
          <SkBar width="92px" height="9px" />
          <SkBar width="80px" height="9px" />
        </div>
        <SkBar width="28px" height="28px" radius="6px" className="ml-auto" />
      </div>
      <SkBar width="92%" height="9px" />
      <SkBar width="74%" height="9px" />
    </ChatCard>
  );
}
